<script>
import { defineAsyncComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { componentStore, componentGlobalConfig } from "@common/componentStore/componentStore.js";
import { library } from "@common/library/library";
import baseComponentConfigs from "@components/devDash/data/baseComponentConfigs.js";

const editorSettings = {};

const componentListConfig = {
    cId: "componentList",
    cType: "componentList",
    cTitle: "Components List",
    cTemplate: `<div class="component-list">
                    <div class="component-list-button-area">
                        <div class="new-control">
                            <control name="NEW_COMPONENT"></control>
                        </div>
                        <control name="RELOAD_COMPONENTS"></control>
                    </div>
                    <condensed-filter
                        :prop-filter-options="filterOptions"
                        :prop-default-applied-filters="defaultAppliedFilters"
                        :prop-item-label="'components'"
                        cId="condensedFilter__components"
                        :prop-filter-items="components"
                        :prop-result="filteredItems"
                        @update:prop-result="filteredItems = $event"
                        :prop-filtering="filtering"
                        @update:prop-filtering="filtering = $event"></condensed-filter>
                    <div v-show="filtering" class="filtering-message">updating list</div>
                    <control v-show="!filtering" name="COMPONENT_LIST" :items="filteredItems"></control>
                </div>`,
    controls: [
        {
            name: 'NEW_COMPONENT',
            type: "button",
            label: "New Component",
            onclick: "newComponent",
            cssClass: "e-block e-info"
        },
        {
            name: 'RELOAD_COMPONENTS',
            type: 'button',
            label: 'Reload',
            hideLabel: true,
            iconCss: 'e-icons e-reload',
            onclick: 'reloadComponents',
        },
        {
            name: 'COMPONENT_LIST',
            type: 'list',
            itemContentTemplate: `<div v-if="data" class="sub-list-item-container component-item" :class="{'selected-item':component.selectedComponentId===data.cId}" :id="'componentlist-component-id-' + data.interfaceId" @mouseenter="onMouseenter" @mouseleave="onMouseleave">
                    <div class="component-list-main-details">
                        <div class="component-label" @click="component.selectComponent(data)">
                            <span class="component-name">{{data.cTitle}}<span v-if="data.isNew" class="component-status-flag">NEW</span><span v-if="data.unsaved" class="component-status-flag">UNSAVED SETTINGS</span><span v-if="data.isCore" class="component-core">CORE</span></span>
                            <span class="component-id">
                                <span @click.stop="component.copyId(data.cId)">
                                    <span v-show="component.copiedId === data.cId">ID Copied!</span>
                                    <span v-show="component.copiedId !== data.cId">#{{data.cId}}</span>
                                </span>
                            </span>
                        </div>
                        <div class="component-action-button">
                            <settings-cog :component-id="data.cId" editor-enabled="true"></settings-cog>
                        </div>
                    </div>
                    <div class="component-list-more-details">
                        <span class="component-type" :title="'Component Type: ' + data.cType">{{data.cType}}</span>
                        <span class="detail-indicator config-lock" title="Component Lock Status">
                            <ejs-tooltip :content="data.lockedConfig ? 'Locked Config' : 'Unlocked Config'">
                                <span v-if="data.lockedConfig">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-lock" viewBox="0 0 16 16">
                                        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                                    </svg>
                                </span>
                                <span v-else>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-unlock" viewBox="0 0 16 16">
                                        <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5V3a3 3 0 0 1 6 0v4a.5.5 0 0 1-1 0V3a2 2 0 0 0-2-2zM3 8a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H3z"/>
                                    </svg>
                                </span>
                            </ejs-tooltip>
                        </span>
                        <span class="detail-indicator" v-if="data.isUnused" @click="component.markAsUsed(data.cId)">
                            <ejs-tooltip content="This component hasn't been used for 180 days or more">UN</ejs-tooltip>
                        </span>
                        <span class="detail-indicator" v-if="data.dateLastModified">
                            <ejs-tooltip :content="'Modified: ' + data.dateLastModified + ' (' + data.daysSinceLastModify + ' days ago)' + ( data.cModifiedBy ? ' by ' + data.cModifiedBy : '')">M</ejs-tooltip>
                        </span>
                        <span class="detail-indicator" v-if="data.formattedLastLoad">
                            <ejs-tooltip :content="'Last used on: ' + data.formattedLastLoad + ' from ' + data.instance">U</ejs-tooltip>
                        </span>
                        <span class="detail-indicator" v-if="data.instances && data.instances.length">
                            <ejs-tooltip :content="'Instances: ' + data.instances.join(', ')">{{data.instances.length}}</ejs-tooltip>
                        </span>
                        <span class="detail-indicator" v-if="data.outOfDate">
                            <ejs-tooltip :content="'Out of sync: ' + data.outOfDate + ' day(s)'">O {{data.outOfDate}}</ejs-tooltip>
                        </span>
                    </div>
                </div>`
        },
    ],
    layoutDatamap: [
        {
            "property": "filteredItems", "from": "componentList", "to": "thisStore", "as": "filteredComponents"
        },
        {
            "property": "selectedComponentId", "from": "componentList", "to": "thisStore", "as": "selectedComponentId"
        }
    ]
};

const componentListCmp = {
    editorSettings: editorSettings,
    name: 'componentList',
    globalMethods: ['selectComponent', 'reloadComponents'],
    data() {
        return {
            selectedComponent: false,
            selectedComponentId: false,
            filteredItems: [],
            filtering: false,
            copiedId: false,
            hovered: false,
            defaultAppliedFilters: {
                isUnused: ['false']
            }
        };
    },
    watch: {
        filtering() {
            this.setScrollPosition();
        }
    },
    computed: {
        filterOptions() {
            let tmpFilters = {
                all: {
                    label: "All Keywords",
                    required: true,
                    type: "string"
                },
                any: {
                    label: "Any Keyword",
                    required: true,
                    type: "string"
                },
                ...library.cloneObj(componentGlobalConfig),
                cCreatedBy: {
                    label: 'Created By',
                    required: true,
                    type: 'string'
                },
                cLastModifyUnixDay: {
                    label: 'Last Modified',
                    required: true,
                    type: 'date'
                },
                lastLoad: {
                    label: 'Last Used',
                    required: true,
                    type: 'date'
                },
                instances: {
                    label: "Instance",
                    type: "dropdown",
                    required: true,
                    disabled: true
                },
                datasets: {
                    label: "Datasets",
                    type: "dropdown",
                    required: true,
                    disabled: true
                },
                cModifiedBy: {
                    label: 'Modified By',
                    required: true,
                    type: 'string'
                },
                isUnused: {
                    label: 'Show Unused',
                    required: true,
                    disabled: true,
                    type: 'dropdown',
                    maximumSelectionLength: 1,
                    filterType: 'Contains',
                    showFilterLinksInSummary: true,
                    exactMatch: false,
                    values: [
                        {
                            text: "All", value: 'all', linkText: 'all', switchTo: 'false'
                        },
                        {
                            text: "Only Used", value: 'false', linkText: 'used', switchTo: 'true'
                        },
                        {
                            text: "Only Unused", value: 'true', linkText: 'unused', switchTo: 'all'
                        },
                    ]
                }
            };
            let exclusions = ['cDescription', 'cTemplate'];
            for (let ccKey in tmpFilters) {
                if (ccKey === 'length') continue;
                if (exclusions.indexOf(ccKey) > -1) {
                    delete tmpFilters[ccKey];
                }
            }
            return tmpFilters;
        },
        baseComponents() {
            let baseComponents = {};
            for (let i in baseComponentConfigs) {
                baseComponents[baseComponentConfigs[i].cId] = library.cloneObj(baseComponentConfigs[i]);
            }
            return baseComponents;
        },
        components() {
            let components = library.cloneObj(componentStore.getComponents());
            // let components = library.cloneObj(this.components2);
            for (let cId in components) {
                if (cId === 'length') continue;
                if (cId.indexOf('__') !== -1 || components[cId].cType === 'pdf' || (this.baseComponents[cId] && this.baseComponents[cId].hideFromDevDash)) {
                    delete components[cId];
                    continue;
                }
                delete components[cId].cTemplate;
                delete components[cId].controls;
                components[cId].interfaceId = cId.replace(/[\W]+/g, "-");
            }
            return library.sortObject(Object.values(components), ['cType', 'cTitle', 'cId']);
        }
        // displayedComponents() {
        //     return this.filteredItems.slice(0, 20);
        // }
    },
    methods: {
        setScrollPosition() {
            let scrollToPosition = (elemId, toTop) => {
                setTimeout(() => {
                    let elem = document.getElementById(elemId);
                    if (elem) {
                        if (toTop) elem.scrollTo({ top: 0, behavior: 'smooth' });
                        else elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 2000); // NOSONAR
            };
            if (!this.selectedComponent) {
                scrollToPosition('COMPONENT_LIST_componentList', true);
                return;
            }
            let component = this.selectedComponent;
            let elem = document.getElementById('componentlist-component-id-' + component.interfaceId);
            if (elem) {
                scrollToPosition('componentlist-component-id-' + component.interfaceId);
            }
            else {
                this.selectedComponent = false;
                this.selectedComponentId = false;
                scrollToPosition('COMPONENT_LIST_componentList', true);
            }
        },
        async onLoad() {
            componentStore.getList(true);
        },
        reloadComponents() {
            componentStore.getList(true, true);
        },
        selectComponent: async function (component, reloadList) {
            if (reloadList) {
                await componentStore.getList(true, true);
                // await this.getComponents();
            }
            if (component === false) {
                this.selectedComponent = false;
                this.selectedComponentId = false;
                return;
            }
            let cId = component.cId;
            let selection = this.components.find((comp) => comp.cId === cId);
            if (typeof selection === 'object' && !selection.configLoaded) {
                componentStore.getConfig(cId);
                // const config = await componentStore.getConfig(cId);
                // this.components[cId] = config;
                // this.components[cId].configLoaded = true;
            }
            if (selection) {
                this.selectedComponent = false;
                this.selectedComponentId = false;
                this.selectedComponent = selection;
                this.selectedComponentId = cId;
                this.setScrollPosition();
            } else {
                this.selectedComponent = false;
                this.selectedComponentId = false;
            }
        },
        newComponent: async function () {
            let cId = await actionStore.createComponent(false, false, { dialogTitle: 'Create New Component', submitButtonLabel: 'Create Component' });
            // await componentStore.getList(true, true);
            let component = this.components.find((comp) => comp.cId === cId);
            if (component) {
                this.selectComponent(component);
            }
        },
        copyId(text) {
            this.copiedId = text;
            library.copyTextToClipboard(text);
            setTimeout(() => {
                this.copiedId = false;
            }, 2000); // NOSONAR
        },
        async markAsUsed(cId) {
            let response = await this.sDs.post('sds_setComponentUsed', cId);
            if (response.status === 'success') {
                this.reloadComponents();
            }
        }
    },
    components: {
        componentDetails: defineAsyncComponent(() => import('./componentDetails.vue')),
        condensedFilter: defineAsyncComponent(() => import('@common/condensedFilter/condensedFilter.vue')),
        control: defineAsyncComponent(() => import('@common/control/control.vue'))
    }
};

componentStore.initComponent(componentListCmp, componentListConfig);
export { componentListConfig, componentListCmp };
export default componentListCmp;

</script>

<style>
    .component-item {
        width: 100%;
        margin-bottom: 2px;
    }
    .component-item:hover {
        background: #f3d9dd;
    }
    .component-list-main-details {
        display: flex;
    }
    .component-label{
        flex-grow: 9;
        padding: 5px;
        overflow: hidden;
        font-weight: bold;
        cursor: pointer;
    }
    .component-list-more-details {
        padding: 4px 5px 8px 5px;
        border-top: 1px solid #d9d9d9;
        font-size: 14px;
        color: #e91a84;
    }
    .detail-indicator {
        text-align: center;
        cursor: pointer;
        padding: 0 6px;
        min-width: 22px;
        height: 22px;
        line-height: 22px;
        background: rgba(255,255,255, .6);
        border-radius: 9px;
        display: block;
        float: right;
        margin-left: 5px;
    }
    .detail-indicator:hover {
        background: white;
    }
    .detail-indicator.config-lock {
        width: 26px;
    }
    .component-item.selected-item {
        background: pink;
    }
    .component-name {
        display: block;
        font-size: 15px;
        margin-bottom: 5px;
        padding-left: 2px;
        white-space: nowrap;
        color: black;
    }
    .component-id {
        display: inline;
        font-size: 15px;
        color: #555;
        white-space: nowrap;
        text-decoration: underline;
    }
    :deep(.e-content > div) {
        margin:10px 0 10px 10px;
    }
    .publish-action {
        margin-right: 3px;
    }
    .component-item {
        background: #eee;
    }
    .component-name-local {
        color: darkgreen;
    }
    #COMPONENT_LIST_componentList {
        max-height: 500px;
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 2px;
    }
    .component-status-flag {
        margin-left: 10px;
        color: #e91a84;
        font-weight: bold;
    }
    .component-core {
        margin-left: 10px;
        color: #e91a84;
        font-weight: bold;
    }
    .component-list-button-area {
        display: flex;
        margin-bottom: 15px;
    }
    .component-list-button-area div.new-control {
        flex-grow: 9;
        margin-right: 10px;
    }
    .e-reload:before {
        content: '\e99d' !important;
    }
    .component-action-button {
        display: flex;
        flex-direction: column;
    }
    .component-action-button .control {
        flex-grow: 1;
    }
    .component-action-button .editor-cog-wrapper {
        width: 36px;
        margin-top: 2px;
        height: auto;
    }
    .component-action-button .editor-cog-button {
        width: 23px;
        height: 23px;
        background: #d0d0d0;
        color: rgba(0, 0, 0, 87%);
        border: 0 none;
        border-radius: 0;
        vertical-align: middle;
        border-left: 1px solid white;
        border-bottom: 1px solid #eee;
        padding: 0;
        text-align: center;
        box-shadow: none;
    }
    .component-action-button .editor-cog-button:hover, .e-btn.preview-link.component-actions:hover {
        background: #f0f0f0;
        box-shadow: -1px -1px 2px rgb(0 0 0 / 10%);
    }
    .component-action-button .editor-cog-button .e-icons.e-cog {
        position: relative;
        top: -2px;
    }
    #NEW_COMPONENT_componentList-popup {
        height: 200px;
        overflow: auto;
    }
    .filtering-message {
        padding: 10px;
        margin: 5px;
        text-align: center;
        font-weight: bold;
        background: #d3dfea;
        border: 1px solid #8cb8c9;
        color: #596787;
        border-radius: 4px;
        font-size: 13px;
    }
    .e-btn.preview-link.component-actions {
        background: #d0d0d0;
        border-left: 1px solid white;
    }
</style>
